/**
 * Atlas access-list thresholds and the entry-state contract.
 *
 * These numbers decide what the UI shouts about AND what the resolver reports, so
 * they live in one place. A banner that warns at 180 while the API classifies at
 * 190 is not a cosmetic bug — it is a page that tells you two different things
 * about whether production is about to lose database access.
 */

/**
 * The hard MongoDB Atlas limit on entries in a project's IP access list. Not a
 * budget we chose: past this, `POST …/accessList` is REJECTED, so the next node to
 * come up never reaches the database. Every capacity bar is `n / ATLAS_CAP`, never
 * normalised to the largest project — normalising would make the cap invisible,
 * which is the whole failure this feature exists to prevent.
 */
export const ATLAS_CAP = 200;

/** Amber. Early enough that there is still room to clean up before adds fail. */
export const ATLAS_WARN = 180;

/** Red. Effectively out of room — a single node pool resize can clear this. */
export const ATLAS_CRIT = 195;

/**
 * An agent silent longer than this has stopped pushing its node IP.
 *
 * The damage is quiet: the reported set freezes, and the page keeps showing a stale
 * desired state as though it were current. Nothing errors — the numbers just stop
 * being true.
 */
export const ATLAS_AGENT_STALE_MIN = 30;

/** Reported IPs older than this are `stale`: reported once, nothing since. */
export const ATLAS_ENTRY_STALE_DAYS = 30;

export type AtlasLevel = 'ok' | 'warn' | 'crit';

/** `null` = never read (no credentials). Unknown is NOT healthy — the caller must
 *  render it as unknown rather than falling through to the ok branch. */
export function atlasLevel(live: number | null | undefined): AtlasLevel | null {
  if (live == null) return null;
  if (live >= ATLAS_CRIT) return 'crit';
  if (live >= ATLAS_WARN) return 'warn';
  return 'ok';
}

/**
 * Where an entry in the live Atlas list came from, and whether anything still
 * stands behind it. This is the diff the old two-table page made the operator do in
 * their head.
 *
 *  - `in_sync`   — in Atlas, and an agent reports it. The healthy case.
 *  - `no_agent`  — in Atlas, nothing reports it. Occupies a slot for nothing; this
 *                  is what "reclaimable" counts.
 *  - `stale`     — reported, but not seen for ATLAS_ENTRY_STALE_DAYS. Probably a
 *                  dead node, but the agent's silence could also be the cause, so it
 *                  is not the same finding as `no_agent`.
 *  - `manual`    — added by hand in Atlas, outside this system. Legitimate (bastions,
 *                  offices) and must never be reported as reclaimable.
 *  - `open_to_all` — a `/0` CIDR: the list is open to the internet, so the other 199
 *                  entries are decoration. Not in the original state table; it came
 *                  out of the prototype's own fixtures and is too important to drop.
 */
export type AtlasEntryState = 'in_sync' | 'no_agent' | 'stale' | 'manual' | 'open_to_all';

/** A CIDR that admits every address. `/0` is the only prefix that can do it, in
 *  either family, so the suffix test is exact rather than a heuristic. */
export function isOpenToAll(cidr: string): boolean {
  return cidr.trim().endsWith('/0');
}

export const ATLAS_ENTRY_STATE_LABELS: Record<AtlasEntryState, string> = {
  in_sync: 'in sync',
  no_agent: 'no agent',
  stale: 'stale',
  manual: 'manual',
  open_to_all: 'open to all',
};

/** States whose slot could be freed. `manual` is excluded on purpose — a bastion
 *  entry has no agent by design, and offering to reclaim it invites an outage. */
export const ATLAS_RECLAIMABLE_STATES: AtlasEntryState[] = ['no_agent', 'stale'];
